import { useQuery, useQueryClient } from '@tanstack/react-query';

// redux
import { mapDispatchProps, mapStateToProps } from '../../../../store/store';
import { connect } from 'react-redux';
import Button from '../../../../components/button';
import { ROUTES } from '../../../../routes';
import { useNavigate, useParams } from 'react-router';
import API from '../../../../api';

import OnAfterModalClosed from '../../../../libraries/onAfterModalClosed';
import Builder from '../builder';
import Util from '../util';
import { useLanguage } from '@languages/index';

const SNSCelebDetail = (props: any) => {
  const language = useLanguage();
  const languageTexts = { ...language.common, ...language.pages.sns };
  const params = useParams();
  const celebId = params.celebId;

  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const onAfterModalClosed = new OnAfterModalClosed(props);

  /// state

  /// query
  // fetch brand
  const fetchBrandRes = useQuery({
    queryKey: ['fetch_brand'],
    queryFn: async () => {
      try {
        const res = await API.Brand.show({ brandToken: props.auth.token });
        props.setBrand(res.data.brand);

        return res;
      } catch (error) {
        props.showModal({
          hideCloseButton: true,
          text: languageTexts.celeb_detail_text_1,
        });
      }
    },
  });

  // fetch celeb review
  const fetchCelebRes = useQuery({
    queryKey: ['fetch_celeb_review', fetchBrandRes.data?.brand?.id, celebId],
    queryFn: async () => {
      try {
        const res = await API.BrandSns.fetchCelebrities({
          brandToken: props.auth.token,
          where: { id: celebId },
        });
        return res.data.reviews[0];
      } catch (error) {
        props.showModal({
          hideCloseButton: true,
          text: languageTexts.celeb_detail_text_2,
        });
      }
    },
    enabled: !!fetchBrandRes.data,
  });
  const dirtyfetchCeleb = () =>
    queryClient.removeQueries({
      queryKey: ['fetch_celeb_review', fetchBrandRes.data?.brand?.id, celebId],
    });

  /// sns install check
  if (
    fetchBrandRes.data &&
    fetchBrandRes.data.brand &&
    fetchBrandRes.data.brand.hasExperiencedSnsInstall &&
    !fetchBrandRes.data.brand.snsPassInstalledAt
  ) {
    navigate(ROUTES.SNS_PAYMENT_COMPLETE.PATH);
  }

  /// other functions
  const _setCelebPublishState = async (value: boolean) => {
    const celeb = fetchCelebRes.data;
    if (!celeb || !!celeb.isPublished === value) return;

    try {
      props.showModal({
        hideCloseButton: true,
        hideConfirmButton: true,
        text: languageTexts.celeb_detail_text_3,
      });

      const res = await API.BrandSns.updateCelebrity(celeb.id, {
        brandToken: props.auth.token,
        isPublished: value,
      });

      props.closeModal();
    } catch (error) {
      onAfterModalClosed.set(() => {
        props.showModal({
          hideCloseButton: true,
          text: languageTexts.celeb_detail_text_4,
        });
      });
      props.closeModal();
    }

    dirtyfetchCeleb();
  };

  const _buildProducts = () => {
    const celeb = fetchCelebRes.data;
    if (!celeb) return null;

    return celeb.products.map((product) => (
      <div
        key={product.id}
        className="mb-[15px] flex flex-row justify-start items-center"
      >
        <img
          className="mr-[18px] w-[77px] h-[77px] object-cover"
          src={Util.makeImageUrl('product/thumb-sm', product.thumbUrl)}
          alt={product.name}
        />

        <div className="flex flex-col grow overflow-hidden">
          <span className="font-bold text-[15px] whitespace-nowrap overflow-hidden overflow-ellipsis">
            {product.name}
          </span>

          <span
            className="mt-[5px] text-black text-[15px] font-normal"
            style={{ wordBreak: 'keep-all' }}
          >
            {languageTexts._wear(product.sizeName)}
          </span>

          <div className="mt-[5px] flex flex-row justify-between">
            <a
              className="cursor-pointer text-[15px] text-[#0066FF] underline whitespace-nowrap truncate text-ellipsis overflow-hidden max-w-[350px]"
              onClick={() => {
                Util.onLinkClick(product.url);
              }}
            >
              {`${Util.getUrlText(product.url)}`}
            </a>

            <span
              className="underline text-[15px] text-[#999999] cursor-pointer hover:text-black"
              onClick={() => {
                Util.showCelebProductUrlEditModal(
                  props,
                  product.productId,
                  (res) => {
                    dirtyfetchCeleb();
                  },
                  languageTexts.right_side_text_1,
                  languageTexts.right_side_text_2,
                );
              }}
            >
              {languageTexts.change2}
            </span>
          </div>
        </div>
      </div>
    ));
  };

  /// build main
  const build = () => {
    if (!fetchBrandRes.data || !fetchCelebRes.data) return null;

    if (!props.brand?.hasExperiencedSnsInstall || !props.brand?.remainSnsPass)
      return null;

    const celeb = fetchCelebRes.data;

    return (
      <div className="flex flex-col items-center w-[1025px] mx-auto my-[50px]">
        <div className="flex flex-row w-full">
          {Builder.buildBackwardText((e) => {
            navigate(ROUTES.SNS_INDEX.PATH);
          })}
        </div>

        <div style={{ height: '59px' }} />

        <div className="flex flex-row justify-start w-full">
          {/* left-side: title and images */}
          <div className="flex flex-col justify-start w-[400px]">
            {/* title */}
            <span className="text-[15px] text-[#000000] mb-[16px]">
              {celeb.title}
            </span>

            {/* images */}
            {celeb.images.map((img) => (
              <img
                key={img.id}
                className="object-contain w-[400px] h-[400px]"
                src={Util.makeImageUrl('brand_sns/thumb-md', img.imgUrl)}
              />
            ))}
          </div>

          {/* right-side */}
          <div className="flex flex-col justify-start grow ml-[50px]">
            <div className="flex flex-row justify-start items-center w-full">
              {/* publish and edit */}
              <div className="w-[96px] h-[40px]">
                <Button
                  className="py-0 h-full flex justify-center items-center"
                  borderColor={celeb.isPublished ? '#000000' : '#E8E8E8'}
                  buttonColor={celeb.isPublished ? '#000000' : 'WHITE'}
                  textColor={celeb.isPublished ? 'WHITE' : 'BLACK'}
                  text={languageTexts.public}
                  textClassName={'text-[14px]'}
                  onClick={() => _setCelebPublishState(true)}
                />
              </div>
              <div className="w-[96px] h-[40px]">
                <Button
                  className="py-0 h-full flex justify-center items-center"
                  buttonColor={celeb.isPublished ? 'WHITE' : '#E8E8E8'}
                  borderColor={celeb.isPublished ? '#E8E8E8' : '#E8E8E8'}
                  text={languageTexts.private}
                  textClassName={'text-[14px]'}
                  onClick={() => _setCelebPublishState(false)}
                />
              </div>
              <span className="grow" />
              <span
                className="text-[14px] text-[#000000] underline cursor-pointer"
                onClick={(e) => {
                  navigate(ROUTES.SNS_UPLOAD_CELEB.PATH + `?id=${celeb.id}`);
                }}
              >
                {languageTexts.celeb_detail_text_7}
              </span>
            </div>

            {/* publish and edit */}
            <span className="mt-[40px] mb-[14px] font-bold text-[22px] text-[#000000]">
              {languageTexts.celeb_detail_text_8}
            </span>

            {_buildProducts()}
          </div>
        </div>
      </div>
    );
  };

  return build();
};

export default connect(mapStateToProps, mapDispatchProps)(SNSCelebDetail);
